import {
	Component,
	ComponentFactoryResolver,
	ComponentRef,
	OnDestroy,
	OnInit,
	Type,
	ViewChild,
	ViewContainerRef
} from '@angular/core';
import { BentoModalConfirmationCloseReason, BentoModalConfirmationService } from '@bento/bento-ng';
import { MigrationStatusComponent } from '../migration-status/migration-status.component';
import { MigrationContainerComponent } from '../migration-container/migration-container.component';
import { NetclientMigrationComponent } from '../netclient-migration/netclient-migration.component';
import { LoadComponentEventService } from '../migration-products/load-component-event.service';
import { LoadProduct } from './load-product';
import { TranslateService } from '@ngx-translate/core';
import { hasFirmLicense } from '@onviobr/services/permission';
import { MigrationThirdPartyAppsComponent } from '../migration-third-party-apps/migration-third-party-apps.component';
import { MigrationExpandComponentService } from './migration-expand-component.service';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { MigrationSource } from '../migration-reusable-components/migration-source.enum';
import { ParametrizedBentoSideNavItem } from '../migration-reusable-components/parametrized-bento-side-nav-Item';

@Component({
	selector: 'app-migration-side-nav',
	templateUrl: './migration-side-nav.component.html',
	styleUrls: ['./migration-side-nav.component.css']
})
export class MigrationSideNavComponent implements OnInit, OnDestroy {
	// public and resigned for testing
	// FRPs page https://ci.int.onvio.us/tools/#/feature-resource-management/ng/permissions
	public hasFRPLicense = hasFirmLicense; // LICENSES tab
	hasInternalInternal = false;
	hasOnvioUSEssentials = false;

	private unsubscribe = new Subject();

	activeProduct: ParametrizedBentoSideNavItem<LoadProduct>;

	@ViewChild('productMigrationHost', {static: true,  read: ViewContainerRef}) migrationHost: ViewContainerRef;
	@ViewChild('migrationExpandComponentHost', {static: true, read: ViewContainerRef}) expandComponentHost: ViewContainerRef;
	isComponentExpanded = false;

	currentMigrationComponentRef: ComponentRef<any>;
	currentExpandComponentRef: ComponentRef<any>;

	/**
	 * Tabs that expected to be displayed regardless of permissions from very beginning of sidenav list.
	 */
	items: ParametrizedBentoSideNavItem<LoadProduct>[];

	constructor(
		public modalService: BentoModalConfirmationService,
		private componentFactoryResolver: ComponentFactoryResolver,
		private loadComponentEventService: LoadComponentEventService,
		private translate: TranslateService,
		private migrationExpandComponentService: MigrationExpandComponentService,
	) {
	}

	async ngOnInit() {
		this.subscribeToExpandComponent();
		await this.calculateItems();
		this.loadMigrationComponent(this.activeProduct.data.component);
		this.loadComponentEventService.subscriber$
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((migrationSource: MigrationSource) => {
				this.getItemByMigrationSource(migrationSource).selected = true;
			});

		const backFromClientCenter = sessionStorage.getItem('backFromClientCenter');
		if (backFromClientCenter) {
			this.getItemByMigrationSource(MigrationSource.NETFIRM).selected = true;
			sessionStorage.removeItem('backFromClientCenter');
		}
	}

	public async calculateItems(): Promise<void> {
		this.items = this.defaultItems();
		this.activeProduct = this.items[0]; // migration dashboard
		this.hasInternalInternal = await this.hasFRPLicense('Internal.Internal');
		this.items.push(this.getFileCabinetCSItem());
		if (this.hasInternalInternal) {
			this.items.push({
				label: this.translate.instant('onvio.setup.migrations.practicecs'),
				data: new LoadProduct(MigrationContainerComponent, MigrationSource.PRACTICECS)
			});
		}
		this.items.push({
			label: this.translate.instant('onvio.setup.migrations.thirdPartyApps'),
			data: new LoadProduct(MigrationThirdPartyAppsComponent, MigrationSource.TAXCOMPETITOR)
		});
		// this statement forces the list to refresh
		this.items[0].selected = true;
	}

	private defaultItems(): ParametrizedBentoSideNavItem<LoadProduct>[] {
		return [
			{
				label: this.translate.instant('onvio.setup.migrations.dashboard'),
				data: new LoadProduct(MigrationStatusComponent, MigrationSource.ALL), selected: true
			},
			{
				label: this.translate.instant('onvio.setup.migrations.netclientcs'),
				data: new LoadProduct(NetclientMigrationComponent, MigrationSource.NETFIRM)
			},
			{
				label: this.translate.instant('onvio.setup.migrations.ultrataxcs'),
				data: new LoadProduct(MigrationContainerComponent, MigrationSource.ULTRATAX)
			},
		];
	}

	private getFileCabinetCSItem(): ParametrizedBentoSideNavItem<LoadProduct> {
		if (this.hasInternalInternal) {
			return {
				label: this.translate.instant('onvio.setup.migrations.filecabinetcs'),
				data: new LoadProduct(MigrationContainerComponent, MigrationSource.FILECABINET)
			};
		} else {
			return {
				label: this.translate.instant('onvio.setup.migrations.filecabinetcs'),
				data: new LoadProduct(null, MigrationSource.FILECABINET, '#/setup/migration-results')
			};
		}
	}

	private subscribeToExpandComponent() {
		this.migrationExpandComponentService.componentChange$
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(this.loadExpandComponent.bind(this));
	}

	onSelect(item: ParametrizedBentoSideNavItem<LoadProduct>) {
		const component = item.data.component;
		const activeProductCodeName = this.activeProduct.data.migrationSource;
		if (component) {
			if (activeProductCodeName !== item.data.migrationSource) {
				this.activeProduct = item;
				this.loadMigrationComponent(component);
			}
		} else if (item.data.urlToRedirect) {
			const message = this.translate.instant('onvio.setup.migrations.product.redirection.modal.message', {productName: item.label });
			this.modalService
				.open(message, {
					titleText: this.translate.instant('onvio.setup.migrations.product.redirection.modal.title'),
					iconName: 'bento-icon-warning-filled',
					iconColorType: 'info',
					closeButtonText: this.translate.instant('onvio.setup.migrations.product.redirection.modal.closebtn.text')
				}, {
					ariaLabelledBy: '',
					ariaDescribedBy: '',
					backdrop: false,
					beforeDismiss: () => true, // return true to allow dismiss
					centered: false,
					container: '.ng-bento',
					injector: null,
					keyboard: true,
					scrollable: false,
					size: 'lg',
					windowClass: `${item.data.migrationSource.toLowerCase()}-modal`,
					backdropClass: ''
				})
				.result.then(
					(result: BentoModalConfirmationCloseReason) => {
						if (result === BentoModalConfirmationCloseReason.Confirm) {
							window.open(item.data.urlToRedirect, '_blank');
						}
						this.getItemByMigrationSource(activeProductCodeName).selected = true;
					},
					reason => {
						console.log(`Dismissed ${reason}`);
						this.getItemByMigrationSource(activeProductCodeName).selected = true;
					}
				);
		}
	}

	private loadMigrationComponent(component: Type<any>): void {
		const previousComponentRef = this.currentMigrationComponentRef;
		this.currentMigrationComponentRef = this.loadComponent(component, this.migrationHost);
		if (previousComponentRef) {
			previousComponentRef.destroy();
		}
		if (this.currentMigrationComponentRef) {
			// `selectedProduct` is used for product differentiation in `MigrationContainerComponent`
			this.currentMigrationComponentRef.instance.selectedProduct = this.activeProduct;
		}
	}

	private loadExpandComponent(component: Type<any>): void {
		if (component) {
			this.isComponentExpanded = true;
			this.currentExpandComponentRef = this.loadComponent(component, this.expandComponentHost);
		} else {
			this.isComponentExpanded = false;
			if (this.currentExpandComponentRef) {
				this.currentExpandComponentRef.destroy();
			}
		}
	}

	/**
	 * Load a component dynamically.
	 *
	 * @param component that needs to be dynamically loaded.
	 * @param hostViewContainerRef placeholder to load the component to.
	 */
	loadComponent(component, hostViewContainerRef: ViewContainerRef): ComponentRef<any> {
		// resolve the component
		if (hostViewContainerRef) {
			const componentFactory = this.componentFactoryResolver.resolveComponentFactory(component);
			hostViewContainerRef.clear();
			return hostViewContainerRef.createComponent(componentFactory);
		} else {
			return null;
		}
	}

	private getItemByMigrationSource(migrationSource: MigrationSource): ParametrizedBentoSideNavItem<LoadProduct> {
		const result = this.items.find(item => item.data.migrationSource === migrationSource);
		if (result) {
			return result;
		}
		throw new Error('Cant get the side-nav item by product name');
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
		if (this.currentMigrationComponentRef) {
			this.currentMigrationComponentRef.destroy();
		}
		if (this.currentExpandComponentRef) {
			this.currentExpandComponentRef.destroy();
		}
	}
}
